import { NetworkStatus, useReactiveVar } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DragEndParams } from "react-native-draggable-flatlist";

import { useCustomMetricSortVariables } from "./useCustomMetricSortVariables";
import { useInfiniteScroll } from "./useInfiniteScroll";
import useRecordUserGoal, { GoalType } from "./useRecordUserGoal";

import {
  Player,
  useWatchlistListMyUserWatchlistsQuery,
  useWatchlistListWatchlistPlayersQuery,
  useWatchListUpdateWatchListPlayerPositionsMutation,
  WatchlistWatchlistMetaFragment,
} from "@/generated/graphql";
import { PlayerCardData } from "@/types/playerCardTypes";
import { MY_WATCHLISTS_PAGE_SIZE } from "@/features/watchlist/constants";
import { useCanAccessFeature } from "@/providers/FeatureFlags";
import { watchListSelectedVar } from "@/stores/player";
import { IMenuSort, watchlistSortVar } from "@/stores/selectionSort";
import { myRosterSelectedVar } from "@/stores/watchlistSelection";

/**
 * Props for useWatchlistManagement hook.
 */
interface UseWatchlistManagementProps {
  accessToken?: string;
  sortValues?: IMenuSort;
}

/**
 * Return type for useWatchlistManagement hook.
 */
interface UseWatchlistManagementReturn {
  watchlists: WatchlistWatchlistMetaFragment[];
  watchlistPlayers: Player[];
  activeWatchlist: WatchlistWatchlistMetaFragment | undefined;
  selectedWatchlistId: string;
  loadWatchlists: boolean;
  loadingPlayers: boolean;
  isRefreshing: boolean;
  handleDragEnd: (params: DragEndParams<PlayerCardData>) => void;
  handleLoadMore: () => void;
  handleRefresh: () => void;
  handleRefreshWatchlist: () => void;
  refetchWatchlistPlayers: () => Promise<unknown>;
}

/**
 * Custom hook for managing watchlist operations including loading, sorting, and updating player positions
 * @param props - Configuration for watchlist management
 * @param props.accessToken - Authentication token for API requests
 * @param props.sortValues - Sort configuration for watchlist players
 * @returns Object containing watchlist data and handler functions
 */
const useWatchlistManagement = ({
  accessToken,
  sortValues,
}: UseWatchlistManagementProps): UseWatchlistManagementReturn => {
  const watchListSelected = useReactiveVar(watchListSelectedVar);
  const myRosterSelected = useReactiveVar(myRosterSelectedVar);
  const isSortPlayerEnable = useCanAccessFeature("globalSort");
  const [playerList, setPlayerList] = useState<Player[]>([]);
  const hasSetInitialWatchlist = useRef(false);

  const [updatePositions] =
    useWatchListUpdateWatchListPlayerPositionsMutation();

  const recordUpdatePlayerPosition = useRecordUserGoal({
    goalType: GoalType.UpdatePlayerPosition,
    value: "",
  });

  // Get custom metric sort variables using shared hook
  const { sortVariables } = useCustomMetricSortVariables(
    sortValues,
    isSortPlayerEnable
  );

  const {
    data: watchlistsData,
    loading: loadWatchlists,
    refetch: refetchWatchlists,
  } = useWatchlistListMyUserWatchlistsQuery({
    variables: { first: MY_WATCHLISTS_PAGE_SIZE },
    skip: !accessToken,
    onCompleted: data => {
      // Don't auto-select watchlist if user is on "My Roster"
      if (myRosterSelected) {
        return;
      }

      watchListSelectedVar(
        watchListSelected ??
          data?.listMyUserWatchlists?.edges?.[0]?.node?.watchlist?.id ??
          ""
      );
    },
  });

  const {
    data: listWatchlistPlayers,
    loading: loadingPlayers,
    networkStatus,
    refetch: refetchWatchlistPlayers,
    fetchMore,
  } = useWatchlistListWatchlistPlayersQuery({
    variables: {
      watchlistId:
        watchListSelected ??
        watchlistsData?.listMyUserWatchlists?.edges?.[0]?.node?.watchlist?.id ??
        "",
      ...sortVariables,
    },
    notifyOnNetworkStatusChange: true,
    skip: !accessToken || !watchListSelected,
  });

  const isRefreshing = networkStatus === NetworkStatus.refetch;

  // Smart loading: only include watchlists loading when no watchlist is selected
  // (waiting for auto-selection). When a watchlist IS selected, only the players
  // query matters — the slow watchlists query should not block the empty state.
  // Fixes SE-3693: empty watchlists took ~5s to show "No Players" because the
  // heavy listMyUserWatchlists query (which embeds all player data) blocked the UI.
  const isPlayerListLoading =
    loadingPlayers || (!watchListSelected && loadWatchlists);

  const watchlists = useMemo(
    () =>
      watchlistsData?.listMyUserWatchlists?.edges?.map(
        edge => edge?.node?.watchlist
      ) ?? [],
    [watchlistsData?.listMyUserWatchlists?.edges]
  );

  const activeWatchlist = watchlists.find(
    item => item.id === watchListSelected
  );

  const firstId = watchlists[0]?.id;

  const players = useMemo(
    () =>
      listWatchlistPlayers?.listWatchlistPlayers?.edges?.map(
        item => item?.node
      ) ?? [],
    [listWatchlistPlayers?.listWatchlistPlayers?.edges]
  );

  const handleDragEnd = useCallback(
    (params: DragEndParams<PlayerCardData>) => {
      if (sortValues?.sortOrder) {
        watchlistSortVar({
          ...sortValues,
          sortOrder: undefined,
        } as IMenuSort);
        return;
      }

      const newPositions = params?.data?.map(
        (watchList: PlayerCardData, index: number) => {
          return {
            playerId: watchList.id,
            position: index,
          };
        }
      );

      if (newPositions.length > 0) {
        updatePositions({
          onCompleted: () =>
            recordUpdatePlayerPosition({
              value: newPositions[0].playerId,
              goalType: GoalType.UpdatePlayerPosition,
            }),
          variables: {
            input: {
              watchlistId: watchListSelected ?? "",
              newPositions,
            },
          },
          refetchQueries: ["WatchlistListWatchlistPlayers"],
        });
      }
    },
    [sortValues, updatePositions, watchListSelected, recordUpdatePlayerPosition]
  );

  const { loadMore: handleLoadMore } = useInfiniteScroll({
    pageInfo: listWatchlistPlayers?.listWatchlistPlayers?.pageInfo,
    fetchMore,
    networkStatus,
  });

  const handleRefresh = useCallback(
    () => refetchWatchlistPlayers().catch(() => {}),
    [refetchWatchlistPlayers]
  );

  const handleRefreshWatchlist = useCallback(
    () => !loadWatchlists && refetchWatchlists().catch(() => {}),
    [loadWatchlists, refetchWatchlists]
  );

  useEffect(() => {
    // Don't auto-select watchlist if user is on "My Roster"
    if (myRosterSelected) {
      return;
    }

    if (
      !activeWatchlist &&
      firstId &&
      !loadWatchlists &&
      !hasSetInitialWatchlist.current
    ) {
      hasSetInitialWatchlist.current = true;
      watchListSelectedVar(firstId);
    }
  }, [activeWatchlist, firstId, loadWatchlists, myRosterSelected]);

  useEffect(() => {
    // Only update if we have data or if we're not loading
    // This prevents flashing empty state during watchlist switches
    if (players.length > 0 || !loadingPlayers) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional flash prevention: only update when data available or loading complete
      setPlayerList(players as Player[]);
    }
  }, [players, loadingPlayers]);

  return {
    watchlists,
    watchlistPlayers: playerList,
    activeWatchlist,
    selectedWatchlistId: watchListSelected ?? "",
    loadWatchlists,
    loadingPlayers: isPlayerListLoading,
    isRefreshing,
    handleDragEnd,
    handleLoadMore,
    handleRefresh,
    handleRefreshWatchlist,
    refetchWatchlistPlayers,
  };
};

export default useWatchlistManagement;
